<template>
  <ModalCard
    v-if="modelValue"
    :title="computedTitle"
    :handle-close="close"
    width="95%"
  >
    <template #body>
      <div class="picker-header">
        <div v-if="showTabs" class="buttons has-addons mr-3 mb-0">
          <button
            class="button is-small"
            :class="{ 'is-primary is-selected': activeTab === 'search' }"
            @click="activeTab = 'search'"
          >
            <span class="icon is-small">
              <i class="fa-solid fa-search" />
            </span>
            <span>Search</span>
          </button>
          <button
            class="button is-small"
            :class="{ 'is-primary is-selected': activeTab === 'create' }"
            @click="activeTab = 'create'"
          >
            <span class="icon is-small">
              <i class="fa-solid fa-plus-circle" />
            </span>
            <span>Create</span>
          </button>
        </div>
        <div
          v-if="typeChoices && typeChoices.length > 1"
          class="select is-small mr-2"
        >
          <select v-model="internalType">
            <option v-for="opt in typeChoices" :key="opt.id" :value="opt.id">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <template v-if="activeTab === 'search'">
          <slot name="search-header">
            <span class="is-size-6 has-text-grey">
              Select {{ displayLabel }}
            </span>
          </slot>
        </template>
        <span v-else class="is-size-6 has-text-grey">
          Create a new {{ displayLabel }}
        </span>
      </div>
      <div v-show="activeTab === 'search'">
        <ListComponent
          :key="`${computedVuexPrefix}-${appName}-${modelName}-${computedModelType}`"
          :app-name="appName"
          :model-name="modelName"
          :model-type="computedModelType"
          :vuex-prefix="computedVuexPrefix"
          :is-compact="true"
          :show-add-controls="false"
          :show-global-search="true"
          :show-owner-controls="true"
          :can-save-settings="false"
          :selection-mode="selectionMode"
          init-ownership="personal"
          @selected="selectedRecords = $event"
        />
      </div>
      <div v-if="hasCreate" v-show="activeTab === 'create'">
        <span
          v-if="!dv || !dv.isMetaReady || !dv.isRecordReady"
          class="is-flex is-justify-content-center has-text-grey p-4"
        >
          <span class="icon is-large is-loading">
            <i class="fa-solid fa-spinner fa-spin fa-3x" />
          </span>
        </span>
        <fieldset v-else :disabled="dv.isSaving" style="display: contents">
          <component :is="detailComponent" :dv="dv" />
        </fieldset>
      </div>
    </template>
    <template #footer>
      <div v-if="activeTab === 'search'">
        <p v-if="exceedsMax" class="has-text-danger is-size-7">
          <span class="icon is-small">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          Please select at most {{ effectiveMaxSelection }}
          {{ effectiveMaxSelection === 1 ? 'record' : 'records' }}.
        </p>
        <BaseButton
          size="small"
          variant="danger"
          outlined
          icon="fa-solid fa-times"
          label="Cancel"
          @click="close"
        />
        <BaseButton
          size="small"
          variant="primary"
          outlined
          icon="fa-solid fa-check"
          label="Confirm"
          :disabled="exceedsMax || selectedRecords.length === 0"
          @click="confirmSearch"
        />
      </div>
      <div v-else>
        <BaseButton
          size="small"
          icon="fa-solid fa-times"
          label="Cancel"
          @click="close"
        />
        <BaseButton
          size="small"
          variant="primary"
          outlined
          icon="fa-solid fa-save"
          :label="`Create ${displayLabel}`"
          :disabled="(dv && dv.hasValidationErrors) || (dv && dv.isSaving)"
          :loading="dv && dv.isSaving"
          @click="confirmCreate"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { computed, provide, ref, watch } from 'vue'

import { useNewView } from '@/composables/useNewView'
import { SELECTION_MODES } from '@/composables/useSelectableRecords'

import SampleView from '@/components/detail/sample/SampleView.vue'
import RecordFieldSections from '@/components/detail/shared/RecordFieldSections.vue'
import ListComponent from '@/components/listings/ListComponent.vue'

const DETAIL_COMPONENTS = {
  studies: RecordFieldSections,
  samples: SampleView,
}

export default {
  name: 'RecordPickerModal',
  components: {
    ListComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: 'search',
    },
    mode: {
      type: String,
      default: 'both',
      validator: (v) => ['both', 'search', 'create'].includes(v),
    },
    appName: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelType: {
      type: String,
      default: 'default',
    },
    typeChoices: {
      type: Array,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    vuexPrefix: {
      type: String,
      default: null,
    },
    maxSelection: {
      type: Number,
      default: 0,
    },
    selectionMode: {
      type: String,
      default: 'select',
      validator: (v) => SELECTION_MODES.includes(v),
    },
  },
  emits: ['update:modelValue', 'update:modelType', 'select'],
  setup(props, { emit }) {
    provide('fieldPage', 'new')
    provide('linkBehavior', 'blank')

    const activeTab = ref(props.mode === 'create' ? 'create' : props.initialTab)
    const selectedRecords = ref([])

    const internalType = ref(props.modelType)

    const effectiveMaxSelection = computed(() =>
      props.selectionMode === 'radio' ? 1 : props.maxSelection,
    )
    const exceedsMax = computed(
      () =>
        effectiveMaxSelection.value > 0 &&
        selectedRecords.value.length > effectiveMaxSelection.value,
    )

    const showTabs = computed(() => props.mode === 'both')
    const hasCreate = computed(
      () => props.mode === 'both' || props.mode === 'create',
    )

    const displayLabel = computed(() => dv?.modelLabel || props.modelName)

    const computedTitle = computed(() => {
      if (props.title) return props.title
      const icon =
        activeTab.value === 'search' ? 'fa-search-plus' : 'fa-plus-circle'
      const action = activeTab.value === 'search' ? 'Search for' : 'Create a'
      return `<span class="icon is-small mr-2"><i class="fa-solid ${icon} fa-xs"></i></span> ${action} ${displayLabel.value}`
    })

    const computedModelType = computed(() => internalType.value.toLowerCase())
    const computedVuexPrefix = computed(
      () => props.vuexPrefix || `${props.modelName}-picker-`,
    )

    const detailComponent = computed(
      () => DETAIL_COMPONENTS[props.modelName] || null,
    )

    // Initialize useNewView only when create mode is available
    let dv = null
    if (props.mode !== 'search') {
      dv = useNewView({
        appName: props.appName,
        modelName: props.modelName,
        modelType: internalType,
      })
    }

    // Reset activeTab and sync internalType when modal opens
    watch(
      () => props.modelValue,
      (isOpen) => {
        if (isOpen) {
          activeTab.value =
            props.mode === 'create' ? 'create' : props.initialTab
          selectedRecords.value = []
          internalType.value = props.modelType
          dv?.resetRecord()
        }
      },
    )

    // Keep internalType in sync when parent changes the prop while modal is open
    watch(
      () => props.modelType,
      (val) => {
        internalType.value = val
      },
    )

    // When type changes inside the modal, reset the create form and notify parent
    watch(internalType, () => {
      dv?.resetRecord()
      emit('update:modelType', internalType.value)
    })

    const close = () => {
      emit('update:modelValue', false)
    }

    const confirmSearch = () => {
      emit('select', selectedRecords.value)
      close()
    }

    const confirmCreate = async () => {
      if (!dv) return
      try {
        const result = await dv.createRecord()
        emit('select', [result])
        close()
      } catch {
        // Error already notified by useNewView
      }
    }

    return {
      activeTab,
      selectedRecords,
      internalType,
      exceedsMax,
      effectiveMaxSelection,
      showTabs,
      hasCreate,
      displayLabel,
      computedTitle,
      computedModelType,
      computedVuexPrefix,
      detailComponent,
      dv,
      close,
      confirmSearch,
      confirmCreate,
    }
  },
}
</script>

<style lang="scss" scoped>
.picker-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding-bottom: var(--space-2);
}
</style>
